<template>
  <div>
    <transition name="fade">
      <div class="pass-fail-buttons" v-if="view === 'pending-evaluation'">
        <div class="d-flex gap-1">
          <PassFailButton
            :useIcon="true"
            :icon="passIcon"
            :onClick="handlePass"
            :onClose="handleClose"
            :avatar="avatar"
            :btnText="'Pass'"
            :buttonStyle="passStyle"
          />
          <PassFailButton
            :useIcon="true"
            :icon="failIcon"
            :onClick="handleFail"
            :onClose="handleClose"
            :avatar="avatar"
            :btnText="'Fail'"
            :buttonStyle="failStyle"
          />
          <PassFailButton
            :useIcon="false"
            :onClick="handleNotApplicable"
            :onClose="handleClose"
            :avatar="avatar"
            :btnText="'N/A'"
            :buttonStyle="naStyle"
          />
        </div>
      </div>
      
      <div
        class="pass-fail-buttons inspection-action-reset-pending reset-pending"
        v-else-if="view === 'reset-pending-evaluation'"
      >
        <EXTooltip
            placement="top"
            title=""
            :description="failed_reason || 'Checkpoint has been reset'"
            :delay="100"
          >
            <ChatterIcon :is_white="false" />
          </EXTooltip>
        <div class="reset-pending-content">
          <div class="d-flex gap-1">
            <PassFailButton
              :useIcon="true"
              :icon="passIcon"
              :onClick="handlePass"
              :onClose="handleClose"
              :avatar="avatar"
              :btnText="'Pass'"
              :buttonStyle="passStyle"
            />
            <PassFailButton
              :useIcon="true"
              :icon="failIcon"
              :onClick="handleFail"
              :onClose="handleClose"
              :avatar="avatar"
              :btnText="'Fail'"
              :buttonStyle="failStyle"
            />
            <PassFailButton
              :useIcon="false"
              :onClick="handleNotApplicable"
              :onClose="handleClose"
              :avatar="avatar"
              :btnText="'N/A'"
              :buttonStyle="naStyle"
            />
          </div>
          <EXTooltip
            placement="top"
            title=""
            :description="resetDescription"
            :delay="100"
          >
            <div class="_reset-footer">
              <v-avatar data-name="avatar" size="20" color="rgb(229 214 209)">
                <img
                  v-if="updated_by?.picture"
                  :src="updated_by.picture"
                  alt="Avatar"
                  class="icon"
                />
                <span
                  v-else-if="updated_by?.name"
                  data-name="avatar-text"
                  class="text"
                  >{{ userInitials }}</span
                >
              </v-avatar>
              <p class="was-reset">Reset</p>
            </div>
          </EXTooltip>
        </div>
      </div>

      <div class="evaluation" v-else-if="view === 'failed-evaluation'">
        <div class="evaluation-with-chatter">
          <EXTooltip
            placement="top"
            title=""
            :description="failed_reason || 'No reason provided'"
            :delay="100"
          >
            <ChatterIcon :is_white="false" />
          </EXTooltip>
          <PassFailButton
            :useIcon="true"
            :icon="failIcon"
            :onClick="handleFail"
            :onClose="handleClose"
            :avatar="avatar"
            :isStateTrue="isFailed"
            :btnText="'Fail'"
            :buttonStyle="failStyle"
            :buttonActiveStyle="failActiveStyle"
            :description="failedDescription"
            :btnStateTrueText="'Failed'"
            :updated_by="updated_by"
          />
        </div>
      </div>

      <div class="evaluation" v-else-if="view === 'notapplicable-evaluation'">
        <div class="evaluation-with-chatter">
          <EXTooltip
            placement="top"
            title=""
            :description="not_applicable_reason || 'No reason provided'"
            :delay="100"
          >
            <ChatterIcon :is_white="false" />
          </EXTooltip>
          <PassFailButton
            :useIcon="false"
            :onClick="handleNotApplicable"
            :onClose="handleClose"
            :avatar="avatar"
            :isStateTrue="isNotApplicable"
            :btnText="'N/A'"
            :buttonStyle="naStyle"
            :buttonActiveStyle="naActiveStyle"
            :btnStateTrueText="'N/A'"
            :description="notApplicableDescription"
            :updated_by="updated_by"
          />
        </div>
      </div>

      <div class="evaluation" v-else-if="view === 'obsolete-manual-evaluation'">
        <div class="evaluation-with-chatter">
          <EXTooltip
            placement="top"
            title=""
            :description="obsolete_reason || 'No reason provided'"
            :delay="100"
          >
            <ChatterIcon :is_white="false" />
          </EXTooltip>
          <PassFailButton
            :useIcon="true"
            :icon="obsoleteAutoIcon"
            :onClick="handleObsolete"
            :onClose="handleClose"
            :avatar="avatar"
            :isStateTrue="isObsolete"
            :isObsolete="false"
            :btnText="'Obsolete'"
            :buttonStyle="obsoleteStyle"
            :buttonActiveStyle="obsoleteActiveStyle"
            :btnStateTrueText="'Obsolete'"
            :updated_by="updated_by"
          />
        </div>
      </div>

      <div class="evaluation" v-else-if="view === 'obsolete-auto-evaluation'">
        <div class="evaluation-with-chatter">
          <EXTooltip
            placement="top"
            title=""
            :description="obsolete_reason || 'Automatically marked as obsolete'"
            :delay="100"
          >
            <ChatterIcon :is_white="false" />
          </EXTooltip>
          <PassFailButton
            :useIcon="true"
            :icon="obsoleteAutoIcon"
            :onClick="handleObsolete"
            :onClose="handleClose"
            :avatar="avatar"
            :isStateTrue="isObsolete"
            :isObsolete="true"
            :btnText="'Obsolete'"
            :buttonStyle="obsoleteStyle"
            :buttonActiveStyle="obsoleteActiveStyle"
            :btnStateTrueText="'Obsolete'"
            :updated_by="updated_by"
          />
        </div>
      </div>

      <div class="evaluation" v-else-if="view === 'blocked-by-quizz-evaluation'">
        <div class="evaluation-with-chatter">
          <EXTooltip
            placement="top"
            title=""
            :description="blockedByQuizzDescription"
            :delay="100"
          >
            <div class="blocked-waiting-evaluation">
              <img
                :src="require(`@components/assets/QuizzImpactOnInspection.svg`)"
              />
              <img
                :src="require(`@components/assets/align-right-01.svg`)"
              />
              <img
                :src="require(`@components/assets/search-refraction.svg`)"
              />
            </div>
          </EXTooltip>
        </div>
      </div>

      <div class="evaluation" v-else-if="view === 'waiting-for-quizz-evaluation'">
        <div class="evaluation-with-chatter">
          <EXTooltip
            placement="top"
            title=""
            :description="waitingForQuizzDescription"
            :delay="100"
          >
            <div class="blocked-waiting-evaluation">
              <img
                :src="require(`@components/assets/QuizzImpactOnInspection.svg`)"
              />
              <img
                :src="require(`@components/assets/zap-fast.svg`)"
              />
              <img
                :src="require(`@components/assets/search-refraction.svg`)"
              />
            </div>
          </EXTooltip>
        </div>
      </div>

      <div class="evaluation reset-evaluation" v-else-if="view === 'reset-blocked-by-quizz-evaluation'">
        <div class="evaluation-with-chatter">
          <EXTooltip
            placement="top"
            title=""
            :description="blocked_by_quizz_reason || 'Blocked by quiz requirement'"
            :delay="100"
          >
            <ChatterIcon :is_white="false" />
          </EXTooltip>
          <EXTooltip
            placement="top"
            title=""
            :description="blockedByQuizzDescription"
            :delay="100"
          >
            <div class="blocked-waiting-evaluation">
              <img
                :src="require(`@components/assets/QuizzImpactOnInspection.svg`)"
              />
              <img
                :src="require(`@components/assets/align-right-01.svg`)"
              />
              <img
                :src="require(`@components/assets/search-refraction.svg`)"
              />
            </div>
          </EXTooltip>
        </div>
      </div>

      <div class="evaluation reset-evaluation" v-else-if="view === 'reset-waiting-for-quizz-evaluation'">
        <div class="evaluation-with-chatter">
          <EXTooltip
            placement="top"
            title=""
            :description="waiting_for_quizz_reason || 'Waiting for quiz completion'"
            :delay="100"
          >
            <ChatterIcon :is_white="false" />
          </EXTooltip>
          <EXTooltip
            placement="top"
            title=""
            :description="waitingForQuizzDescription"
            :delay="100"
          >
            <div class="blocked-waiting-evaluation">
              <img
                :src="require(`@components/assets/QuizzImpactOnInspection.svg`)"
              />
              <img
                :src="require(`@components/assets/zap-fast.svg`)"
              />
              <img
                :src="require(`@components/assets/search-refraction.svg`)"
              />
            </div>
          </EXTooltip>
        </div>
      </div>

      <div class="evaluation" v-else-if="view === 'passed-evaluation'">
        <PassFailButton
          :useIcon="true"
          :icon="passIcon"
          :onClick="handlePass"
          :onClose="handleClose"
          :avatar="avatar"
          :isStateTrue="isPassed"
          :btnText="'Pass'"
          :buttonStyle="passStyle"
          :buttonActiveStyle="passActiveStyle"
          :btnStateTrueText="'Passed'"
          :description="passedDescription"
          :updated_by="updated_by"
        />
      </div>

      <div class="evaluation-evaluation" v-else>
        <div class="evaluation-with-chatter">
          <EXTooltip
            placement="top"
            title=""
            :description="failed_reason || not_applicable_reason || 'No reason provided'"
            :delay="100"
          >
            <ChatterIcon :is_white="false" />
          </EXTooltip>
          <PassFailButton
            :useIcon="true"
            :icon="passIcon"
            :onClick="handlePass"
            :onClose="handleClose"
            :avatar="avatar"
            :isStateTrue="isPassed"
            :btnText="'Pass'"
            :buttonStyle="passStyle"
            :buttonActiveStyle="passActiveStyle"
            :btnStateTrueText="'Passed'"
            :description="passedDescription"
            :updated_by="updated_by"
          />
        </div>
      </div>
    </transition>
  </div>
</template>

<script setup>
import { computed } from 'vue';
import PassFailButton from '../common/PassFailButton.vue';
import EXTooltip from '@components/tooltip/EXTooltip.vue';
import Icon from '@components/icons/Icon.vue';
import ChatterIcon from '@components/ChatterIcon/ChatterIcon.vue';

const props = defineProps({
  onClick: {
    type: Function,
    default: () => {},
  },
  view: {
    type: String,
    default: 'pending-evaluation',
    validator: (value) => {
      const allowedTypes = [
        "pending-evaluation",
        "passed-evaluation",
        "failed-evaluation",
        "notapplicable-evaluation",
        "reset-pending-evaluation",
        "obsolete-manual-evaluation",
        "obsolete-auto-evaluation",
        "blocked-by-quizz-evaluation",
        "waiting-for-quizz-evaluation",
        "reset-blocked-by-quizz-evaluation",
        "reset-waiting-for-quizz-evaluation"
      ];
      return allowedTypes.includes(value);
    },
  },
  isPassed: {
    type: Boolean,
    default: false,
  },
  isFailed: {
    type: Boolean,
    default: false,
  },
  isNotApplicable: {
    type: Boolean,
    default: false,
  },
  isObsolete: {
    type: Boolean,
    default: false,
  },
  isBlockedByQuizz: {
    type: Boolean,
    default: false,
  },
  isWaitingForQuizz: {
    type: Boolean,
    default: false,
  },
  failed_reason: {
    type: String,
    default: '',
  },
  not_applicable_reason: {
    type: String,
    default: '',
  },
  obsolete_reason: {
    type: String,
    default: '',
  },
  blocked_by_quizz_reason: {
    type: String,
    default: '',
  },
  waiting_for_quizz_reason: {
    type: String,
    default: '',
  },
  first_name: {
    type: String,
    default: '',
  },
  last_name: {
    type: String,
    default: '',
  },
  updated_on: {
    type: String,
    default: '',
  },
  updated_by: {
    type: Object,
    default: () => ({}),
  },
  avatar: {
    type: String,
    default: '',
  },
});

const emit = defineEmits([
  'onPass',
  'onFail',
  'onNotApplicable',
  'onObsolete',
  'onClose',
  'onBlockedByQuizz',
  'onWaitingForQuizz',
]);

const passIcon = 'pass_check_circle';
const failIcon = 'fail_cross_circle';
const obsoleteAutoIcon = 'obsolete_lightning_bolt';

const passStyle = {
  backgroundColor: '#17B26A',
  color: '#ffffff',
  border: '1px solid transparent',
  hoverBackgroundColor: '#128650',
};

const passActiveStyle = {
  fontWeight: 500,
  backgroundColor: '#ecfdf3',
  color: '#067647',
  border: '1px solid #aaefc6',
};

const failStyle = {
  backgroundColor: '#D92D20',
  color: '#ffffff',
  border: '1px solid transparent',
  hoverBackgroundColor: '#b32318',
};

const failActiveStyle = {
  fontWeight: 500,
  backgroundColor: '#fef3f2',
  color: '#b42318',
  border: '1px solid #fecdca',
};

const naStyle = {
  backgroundColor: '#504D56',
  color: '#ffffff',
  border: '1px solid transparent',
  hoverBackgroundColor: '#D0D5DD',
};

const naActiveStyle = {
  fontWeight: 500,
  backgroundColor: '#EAECF0',
  color: '#000',
  border: '1px solid #666',
};

const obsoleteStyle = {
  backgroundColor: '#FAFFDF',
  color: '#000000',
  border: '1px solid #666666',
  hoverBackgroundColor: '#F0F5CF',
};

const obsoleteActiveStyle = {
  fontWeight: 500,
  backgroundColor: '#FAFFDF',
  color: '#000000',
  border: '1px solid #666666',
};

const blockedByQuizzStyle = {
  backgroundColor: '#D77F1D',
  color: '#ffffff',
  border: '1px solid transparent',
  hoverBackgroundColor: '#B86B18',
};

const blockedByQuizzActiveStyle = {
  fontWeight: 500,
  backgroundColor: '#FCEEDA',
  color: '#B86B18',
  border: '1px solid #FAD19B',
};

const waitingForQuizzStyle = {
  backgroundColor: '#D77F1D',
  color: '#ffffff',
  border: '1px solid transparent',
  hoverBackgroundColor: '#B86B18',
};

const waitingForQuizzActiveStyle = {
  fontWeight: 500,
  backgroundColor: '#FCEEDA',
  color: '#B86B18',
  border: '1px solid #FAD19B',
};

const formattedDate = computed(() => {
  if (!props.updated_on) return '';
  const date = new Date(props.updated_on);
  const day = date.getDate().toString().padStart(2, '0');
  const monthNames = [
    'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
    'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
  ];
  const month = monthNames[date.getMonth()];
  const year = date.getFullYear();
  const hours = date.getHours().toString().padStart(2, '0');
  const minutes = date.getMinutes().toString().padStart(2, '0');

  return `${day}-${month}-${year} ${hours}:${minutes}`;
});

const passedDescription = computed(() => 
  `Passed by ${props.updated_by?.name} on ${formattedDate.value}`
);

const failedDescription = computed(() => 
  `Failed by ${props.updated_by?.name} on ${formattedDate.value}`
);

const notApplicableDescription = computed(() => 
  `Marked as Not Applicable by ${props.updated_by?.name} on ${formattedDate.value}`
);

const resetDescription = computed(() => 
  `Reset by ${props.updated_by?.name} on ${formattedDate.value}`
);

const blockedByQuizzDescription = computed(
  () => `Blocked by Quizz`
);

const waitingForQuizzDescription = computed(
  () => `Waiting for Quizz`
);

const userInitials = computed(() => {
  if (!props.updated_by?.name) return '';

  const nameParts = props.updated_by.name.trim().split(' ');
  return nameParts
    .filter((n) => n.length > 0)
    .map((n) => n[0])
    .join('')
    .toUpperCase();
});

const handlePass = () => {
  emit('onPass');
};

const handleFail = () => {
  emit('onFail');
};

const handleNotApplicable = () => {
  emit('onNotApplicable');
};

const handleObsolete = () => {
  emit('onObsolete');
};

const handleBlockedByQuizz = () => {
  emit('onBlockedByQuizz');
};

const handleWaitingForQuizz = () => {
  emit('onWaitingForQuizz');
};

const handleClose = () => {
  emit('onClose');
};
</script>

<style scoped>
.pass-fail-buttons {
  display: flex;
  flex-direction: column;
  gap: 6px;
  width: fit-content;
  align-items: center;
}

.inspection-action-reset-pending {
  display: flex;
  flex-direction: row;
  gap: 8px;
  align-items: center;
}

.reset-pending-content {
  width: fit-content;
  padding: 7px 5px 4px 5px;
  border-radius: 6px;
  display: flex;
  flex-direction: column;
  gap: 6px;
  align-items: center;
  background-color: #eaecf0;
}

.was-reset {
  margin-bottom: 0 !important;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  text-align: center;
  color: #000;
}

._reset-footer {
  max-width: 93px;
  display: flex;
  justify-content: center;
  gap: 6px;
  align-items: center;
  margin-top: 8px;
}

.gap-1 {
  gap: 4px;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.5s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.text {
  color: #7f56d9;
  font-weight: 500;
  font-size: 12px;
}


/* Evaluation with comment icon */
.evaluation-with-comment {
  display: flex;
  align-items: center;
  gap: 8px;
}

.comment-icon-container {
  display: flex;
  align-items: center;
  justify-content: center;
}

.evaluation-with-chatter {
  display: flex;
  align-items: center;
  gap: 8px;
}
.blocked-waiting-evaluation {
  background-color: #FF8181;
  border: 1px solid #666666;
  padding: 4px 6px;
  border-radius: 6px;
  display: flex;
  gap: 5px;
}
.blocked-waiting-evaluation img {
  width: 20px;
}
</style>
